import { useMemo } from "react"
import { toast } from "react-toastify"

import { trackEvent } from "~/hooks/useTrackingService"
import { api, type RouterOutputs } from "~/utils/api"
import { showErrorToast } from "~/utils/toaster"
import { showMovedToCollectionToast } from "~/utils/toast-utils"
import { mergeMap } from "~/utils/tsUtils"
import { errorMessageIncludes } from "~/utils/errorUtils"
import {
  usePartialRefreshCollections,
  useRemoveCollectionsFromCache,
} from "~/utils/cacheUtils/collectionCacheHelpers"
import { usePartialRefreshForecasts } from "~/utils/cacheUtils/forecastCacheHelpers"
import { usePartialRefreshStockOutTimes } from "~/utils/cacheUtils/stockOutTimesCacheHelpers"
import { usePartialRefreshVariants } from "~/utils/cacheUtils/variantCacheHelpers"
import { CollectionSource } from "~/models/collection"
import { touchesCollection } from "~/utils/demandInvalidation"
import { invalidateSupplyPlanListReads } from "~/hooks/useSupplyPlan"

export type IVariantCollection = RouterOutputs["collection"]["getCollectionsForVariants"][0]
export type ICollectionDetails = RouterOutputs["collection"]["getAllWithDetails"][0]
export type ICollectionSimple = ICollectionDetails
export type ICollectionExternal = RouterOutputs["collection"]["getExternalCollections"][0]

export type ICollectionGroup = { id: number; name: string; collectionIds: number[] }

const invalidateMembershipQueries = (
  utils: ReturnType<typeof api.useUtils>,
  collectionIds: number[],
) => {
  utils.demand2.getDemandTotals.invalidate(undefined, {
    predicate: touchesCollection(collectionIds),
  })
  utils.demand2.getCollectionVariantIds.invalidate(undefined, {
    predicate: touchesCollection(collectionIds),
  })
  // Unfiltered: a membership change moves the overlap of every collection sharing the variant.
  utils.demand2.getPublishOverlap.invalidate()
  utils.demand2.getPublishOverlapVariants.invalidate()
  utils.forecasts.getVariantForecastForCollections.invalidate()
  utils.sales.getSalesForCollectionPeriodOnVariant.invalidate()
  utils.npi.variants.invalidate()
  utils.productManagement.categorizeVariants.invalidate()
  utils.collection.getNoosCollectionMembershipForVariants.invalidate()
  utils.purchasePlan.outOfSyncVariants.invalidate()
  utils.supplyPlan.getBuysForSupplyPlan.invalidate()
}

export const useGetCollections = (options?: {
  collectionIds?: number[]
  ignoreNoos?: boolean
  allowNoCollections?: boolean
}) => {
  const collectionIds = options?.collectionIds
  const ignoreNoos = options?.ignoreNoos ?? false
  const allowNoCollections = options?.allowNoCollections ?? false

  const { data, ...props } = useGetCollectionsWithDetails()
  const memoizedData = useMemo(() => {
    if (collectionIds && collectionIds.length === 0 && allowNoCollections) {
      return []
    }
    let filtered = data
    if (collectionIds && collectionIds.length > 0) {
      filtered = filtered.filter((collection) => collectionIds.includes(collection.id))
    }
    if (ignoreNoos) {
      filtered = filtered.filter((collection) => collection.collectionType !== "noos")
    }
    return filtered
  }, [data, collectionIds, ignoreNoos, allowNoCollections])
  return { data: memoizedData, ...props }
}

export const useGetCollectionNames = (collectionIds: number[]) =>
  api.collection.getCollectionNames.useQuery({ collectionIds })

export type ICollectionLight = RouterOutputs["collection"]["listLight"][0]

/** Collection id/name/dates/type/group for lookup/display, without the heavy getAllWithDetails payload. */
export const useCollectionsLight = ({ ignoreNoos = false }: { ignoreNoos?: boolean } = {}) => {
  const { data, ...props } = api.collection.listLight.useQuery()
  const memoizedData = useMemo(() => {
    if (!data) return []
    return ignoreNoos ? data.filter((c) => c.collectionType !== "noos") : data
  }, [data, ignoreNoos])
  return { data: memoizedData, ...props }
}

export const useCollectionGroupsLight = () => {
  const { data: collections, ...props } = useCollectionsLight()
  const data = useMemo<ICollectionGroup[]>(() => {
    const map = new Map<number, ICollectionGroup>()
    for (const c of collections) {
      if (c.collectionGroupId && c.collectionGroup?.name) {
        const group = map.get(c.collectionGroupId)
        if (group) {
          group.collectionIds.push(c.id)
        } else {
          map.set(c.collectionGroupId, {
            id: c.collectionGroupId,
            name: c.collectionGroup.name,
            collectionIds: [c.id],
          })
        }
      }
    }
    return Array.from(map.values())
  }, [collections])
  return { data, ...props }
}

export const useGetExternalCollections = () => api.collection.getExternalCollections.useQuery()

export const useGetInactiveExternalCollections = () => {
  const { data: collections, ...rest } = useGetExternalCollections()
  return {
    ...rest,
    data: (collections ?? []).filter((collection) => collection.ignored),
  }
}

export const useGetCollectionsWithDetails = (options?: {
  collectionIds?: number[]
  warehouseId?: string
  channel?: string
  filterOutVariants?: boolean
  preferDemandPlan?: boolean
}) => {
  const { collectionIds, warehouseId, channel, filterOutVariants, preferDemandPlan } = options ?? {}
  const { data, ...props } = api.collection.getAllWithDetails.useQuery({
    warehouseId,
    channel,
    filterOutVariants,
    preferDemandPlan,
  })

  const memoizedData = useMemo(() => {
    if (!collectionIds || collectionIds.length === 0) {
      return data ?? []
    }

    return (data ?? []).filter((collection) => collectionIds.includes(collection.id))
  }, [data, collectionIds])

  return { data: memoizedData, ...props }
}

export type CollectionGroupWithMembers = {
  id: number
  name: string
  collections: ICollectionDetails[]
}

/**
 * Split collections into grouped (by collectionGroupId) and ungrouped.
 * Shared grouping logic used by overview, timeline, and list views.
 */
export function groupCollections(collections: ICollectionDetails[]): {
  groups: CollectionGroupWithMembers[]
  ungrouped: ICollectionDetails[]
} {
  const groupMap = new Map<number, CollectionGroupWithMembers>()
  const ungrouped: ICollectionDetails[] = []

  for (const c of collections) {
    if (c.collectionGroupId && c.collectionGroup?.name) {
      const existing = groupMap.get(c.collectionGroupId)
      if (existing) {
        existing.collections.push(c)
      } else {
        groupMap.set(c.collectionGroupId, {
          id: c.collectionGroupId,
          name: c.collectionGroup.name,
          collections: [c],
        })
      }
    } else {
      ungrouped.push(c)
    }
  }

  return { groups: Array.from(groupMap.values()), ungrouped }
}

/** The fields `fullyCoveredGroupName` reads off a collection. */
export type GroupMembership = {
  id: number
  collectionGroupId: number | null
  collectionGroup: { name: string } | null
}

/**
 * The name of the group `subset` fully covers, or null.
 *
 * Full coverage only: a subset holding three of a group's five drops shares a `collectionGroupId`
 * but does not stand for the group, so naming it after the group would overstate what it covers.
 * A subset spanning several groups, or holding ungrouped collections, has no one name either.
 */
export function fullyCoveredGroupName(
  subset: readonly GroupMembership[],
  all: readonly GroupMembership[],
): string | null {
  const first = subset[0]
  if (!first?.collectionGroupId) return null

  const groupId = first.collectionGroupId
  if (!subset.every((c) => c.collectionGroupId === groupId)) return null

  const memberCount = all.filter((c) => c.collectionGroupId === groupId).length
  if (memberCount !== new Set(subset.map((c) => c.id)).size) return null

  return first.collectionGroup?.name ?? null
}

/**
 * Derive collection groups from ICollectionDetails[].
 * Use this instead of getAllCollectionGroups when details data is already available.
 */
function deriveCollectionGroupsFromDetails(
  collections: ICollectionDetails[] | undefined,
): ICollectionGroup[] {
  if (!collections) return []
  return groupCollections(collections).groups.map((g) => ({
    id: g.id,
    name: g.name,
    collectionIds: g.collections.map((c) => c.id),
  }))
}

export const useCollectionGroups = (warehouseId?: string, channel?: string) => {
  const { data: collections, ...props } = useGetCollectionsWithDetails({ warehouseId, channel })
  const data = useMemo(() => deriveCollectionGroupsFromDetails(collections), [collections])
  return { data, ...props }
}

export const useGetSkuIdsForCollection = (collectionId: number | undefined) => {
  const { data } = api.collection.getSkuIds.useQuery(
    { collectionId: collectionId! },
    { enabled: collectionId !== undefined },
  )

  return data ?? []
}

export const useNoosCollectionMembership = (variantIds: string[], enabled = true) =>
  api.collection.getNoosCollectionMembershipForVariants.useQuery(
    { variantIds },
    { enabled: enabled && variantIds.length > 0 },
  )

export const useCollectionOptions = (collectionIds?: number[], filterExternal = false) => {
  const { data } = useGetCollections({ collectionIds })

  return useMemo(() => {
    return (
      data
        ?.filter((x) => !filterExternal || x.source === CollectionSource.Manual)
        .map((collection) => ({
          label: collection.name,
          value: collection.id,
          type: collection.collectionType,
        })) ?? []
    )
  }, [data, filterExternal])
}

export const useCollectionFilter = () => {
  const { data } = useGetCollections()

  return useMemo(() => {
    return data.map((collection) => ({
      id: collection.id,
      groupId: collection.collectionGroupId,
      groupName: collection.groupName,
      value: collection.name,
    }))
  }, [data])
}

const useRefreshCollectionAndLink = () => {
  const utils = api.useUtils()
  const refreshCollections = usePartialRefreshCollections()

  return async (collectionId: number, linkedCollectionId: number | null | undefined) => {
    // The linked collection's `successorId` now points here, so its row moves too.
    await refreshCollections(
      linkedCollectionId != null ? [collectionId, linkedCollectionId] : [collectionId],
    )
    // A linked collection becomes the new one's comparable, which every demand2 read measures on.
    if (linkedCollectionId != null) void utils.demand2.invalidate()
    else {
      utils.demand2.listCollectionBaselines.invalidate()
      utils.demand2.getCollectionContext.invalidate()
    }
  }
}

export const useActivateCollection = () => {
  const utils = api.useUtils()
  const refreshCollectionAndLink = useRefreshCollectionAndLink()

  return api.collection.activateExternalCollection.useMutation({
    onSuccess: async (_data, variables) => {
      await refreshCollectionAndLink(variables.id, variables.linkedCollectionId)
      utils.collection.getExternalCollections.invalidate()
      toast.success("Collection activated.")
      trackEvent("Collection Activated")
    },
    onError: (error) => {
      showErrorToast(error)
    },
  })
}

export const useCreateCollection = () => {
  const utils = api.useUtils()
  const refreshCollectionAndLink = useRefreshCollectionAndLink()

  return api.collection.create.useMutation({
    onSuccess: async (data, req) => {
      await refreshCollectionAndLink(data.id, req.linkedCollectionId)

      if (req.skuIds && req.skuIds.length > 0) {
        utils.product.salesAndForecasts.invalidate()
        utils.forecasts.getAllForecasts.invalidate()
      }

      utils.demandPlan.getDemandPlanRequirements.invalidate()
      trackEvent("Collection Created")
      toast.success("Collection created.")
    },
    onError: (error) => {
      if (errorMessageIncludes(error, "Unique constraint")) {
        toast.error("Error during collection creation, please try again")
      } else {
        showErrorToast(error)
      }
    },
  })
}

/** What moving this collection's dates would do to its published demand. Null once nothing is at stake. */
export const usePublishedDateChangeImpact = (input: {
  id: number | undefined
  rawStartDate: Date | undefined
  rawEndDate: Date | undefined
}) =>
  api.collection.getPublishedDateChangeImpact.useQuery(
    { id: input.id!, rawStartDate: input.rawStartDate, rawEndDate: input.rawEndDate },
    { enabled: input.id != null },
  ).data ?? null

export const useUpdateCollection = () => {
  const utils = api.useUtils()
  const refreshCollections = usePartialRefreshCollections()
  const refreshForecasts = usePartialRefreshForecasts()
  const refreshVariants = usePartialRefreshVariants()
  const refreshStockOutTimes = usePartialRefreshStockOutTimes()

  return api.collection.update.useMutation({
    onSuccess: async (data, variables) => {
      await refreshCollections([data.id, ...data.relinkedCollectionIds])
      if (data.switched) {
        const { variantIds, settings } = data.switched
        utils.variantTableData.settings.setData(undefined, (oldData) => mergeMap(oldData, settings))
        await Promise.all([
          refreshVariants(variantIds),
          refreshForecasts(variantIds),
          refreshStockOutTimes(variantIds),
        ])
        utils.preOrder.getPreordersForCollectionByVariants.invalidate()
        invalidateMembershipQueries(utils, [data.id, ...data.relinkedCollectionIds])
      }
      utils.collection.get.invalidate()
      if (variables.source) void utils.collection.getExternalCollections.invalidate()
      utils.product.salesAndForecasts.invalidate()
      utils.forecasts.getAllForecasts.invalidate()
      // Supply plans show their collections' names, and fashion ones are listed by collection type
      void invalidateSupplyPlanListReads(utils)
      // The link and the season dates decide the comparable every demand2 read measures on.
      void utils.demand2.invalidate()
      trackEvent("Collection Updated")
      toast.success("Collection updated.")
    },
    onError: (error) => {
      showErrorToast(error)
    },
  })
}

export const useUpdateCollectionGroup = () => {
  const refreshCollections = usePartialRefreshCollections()
  const { data: allCollections } = useGetCollectionsWithDetails()

  return api.collection.updateCollectionGroup.useMutation({
    onSuccess: async (_data, variables) => {
      const collectionIds = allCollections
        .filter((c) => c.collectionGroupId === variables.id)
        .map((c) => c.id)
      await refreshCollections(collectionIds)
      trackEvent("Collection Group Renamed")
      toast.success("Collection group renamed.")
    },
    onError: (error) => {
      showErrorToast(error)
    },
  })
}

export const useBulkRemoveCollections = () => {
  const utils = api.useUtils()
  const removeFromCache = useRemoveCollectionsFromCache()
  const refreshForecasts = usePartialRefreshForecasts()

  return api.collection.bulkRemove.useMutation({
    onSuccess: async (results) => {
      const idsToRemove = [
        ...results.removed.map((r) => r.id),
        ...results.deactivated.map((d) => d.id),
      ]
      const affectedVariantIds = removeFromCache(idsToRemove)
      await refreshForecasts([
        ...new Set([...affectedVariantIds, ...results.unpublishedVariantIds]),
      ])
      utils.collection.get.invalidate()
      utils.collection.getExternalCollections.invalidate()
      utils.product.salesAndForecasts.invalidate()
      void invalidateSupplyPlanListReads(utils)

      const successParts = []
      if (results.removed.length > 0) {
        successParts.push(`${results.removed.length} removed`)
      }
      if (results.deactivated.length > 0) {
        successParts.push(`${results.deactivated.length} deactivated`)
      }

      if (results.removed.length === 1 && results.deactivated.length === 0) {
        toast.success("Collection deleted.")
      } else if (results.deactivated.length === 1 && results.removed.length === 0) {
        toast.success("Collection deactivated.")
      } else if (successParts.length > 0) {
        toast.success(`Collections: ${successParts.join(", ")}.`)
      }

      if (successParts.length > 0) {
        trackEvent("Collections Bulk Removed", {
          "Removed Count": results.removed.length,
          "Deactivated Count": results.deactivated.length,
        })
      }

      if (results.failed.length > 0) {
        const failedNames = results.failed.map((f) => f.name ?? `ID ${f.id}`).join(", ")
        toast.error(`Failed to remove ${results.failed.length} collection(s): ${failedNames}`)
      }

      if (results.cleanupError) {
        toast.error(
          `Collections removed, but forecast recalculation failed: ${results.cleanupError}. ` +
            `Data may be stale. Refresh, or contact support if this persists.`,
          { autoClose: false },
        )
      }
    },
    onError: (error) => {
      showErrorToast(error)
    },
  })
}

export const useCombineCollections = () => {
  const refreshCollections = usePartialRefreshCollections()
  const utils = api.useUtils()

  return api.collection.combineCollections.useMutation({
    onSuccess: async (_data, variables) => {
      // Grouping can lock a budget to the new group
      await utils.budgets.getAll.invalidate()
      await refreshCollections(variables.collectionIds)
      toast.success("Collections combined.")
      trackEvent("Collections Combined")
    },
    onError: (error) => {
      showErrorToast(error)
    },
  })
}

export const useAddCollectionsToGroup = () => {
  const refreshCollections = usePartialRefreshCollections()
  const utils = api.useUtils()

  return api.collection.addGroupToCollections.useMutation({
    onSuccess: async (_data, variables) => {
      // Grouping can lock a budget to the group
      await utils.budgets.getAll.invalidate()
      await refreshCollections(variables.collectionIds)
      toast.success("Collections added to group.")
      trackEvent("Collection Group Updated", {
        Action: "Add Collections",
      })
    },
    onError: (error) => {
      showErrorToast(error)
    },
  })
}

export const useRemoveCollectionsFromGroup = () => {
  const refreshCollections = usePartialRefreshCollections()
  const utils = api.useUtils()

  return api.collection.ungroupCollections.useMutation({
    onSuccess: async (_data, variables) => {
      // Ungrouping may delete or shrink group budgets server-side
      await utils.budgets.getAll.invalidate()
      await refreshCollections(variables.collectionIds)
      toast.success("Collections removed from group.")
      trackEvent("Collection Group Updated", {
        Action: "Remove Collections",
      })
    },
    onError: (error) => {
      showErrorToast(error)
    },
  })
}

export const useAddVariantsToCollection = () => {
  const utils = api.useUtils()
  const refreshCollections = usePartialRefreshCollections()
  const refreshForecasts = usePartialRefreshForecasts()
  const refreshVariants = usePartialRefreshVariants()
  const refreshStockOutTimes = usePartialRefreshStockOutTimes()

  return api.collection.addVariants.useMutation({
    onSuccess: async (data, variables) => {
      await refreshCollections([variables.collectionId])

      utils.variantTableData.settings.setData(undefined, (oldData) =>
        mergeMap(oldData, data.settings),
      )

      await Promise.all([
        refreshVariants(data.variantIds),
        refreshForecasts(data.variantIds),
        refreshStockOutTimes(data.variantIds),
      ])

      toast.success("Variants added to collection.")
      trackEvent("Collection Updated", { Action: "Add Variants" })
    },
    onError: (error) => showErrorToast(error),
    onSettled: (_data, _error, variables) => {
      utils.preOrder.getPreordersForCollectionByVariants.invalidate()
      invalidateMembershipQueries(utils, [variables.collectionId])
    },
  })
}

export const useRemoveVariantsFromCollection = () => {
  const utils = api.useUtils()
  const refreshCollections = usePartialRefreshCollections()
  const refreshForecasts = usePartialRefreshForecasts()
  const refreshVariants = usePartialRefreshVariants()
  const refreshStockOutTimes = usePartialRefreshStockOutTimes()

  return api.collection.removeVariants.useMutation({
    onSuccess: async (data, variables) => {
      // Refresh collection in cache first — guarantees new object refs
      await refreshCollections([variables.collectionId])

      // Update settings immediately
      utils.variantTableData.settings.setData(undefined, (oldData) =>
        mergeMap(oldData, data.settings),
      )

      await Promise.all([
        refreshVariants(data.variantIds),
        refreshForecasts(data.variantIds),
        refreshStockOutTimes(data.variantIds),
      ])

      toast.success("Variants removed from collection.")
      trackEvent("Collection Updated", { Action: "Remove Variants" })
    },
    onError: (error) => showErrorToast(error),
    onSettled: (_data, _error, variables) =>
      invalidateMembershipQueries(utils, [variables.collectionId]),
  })
}

export const useMoveVariantsToCollection = () => {
  const utils = api.useUtils()
  const refreshCollections = usePartialRefreshCollections()
  const refreshForecasts = usePartialRefreshForecasts()
  const refreshVariants = usePartialRefreshVariants()
  const refreshStockOutTimes = usePartialRefreshStockOutTimes()

  return api.collection.moveVariants.useMutation({
    onSuccess: async (data, variables) => {
      await refreshCollections([variables.sourceCollectionId, variables.targetCollectionId])

      await Promise.all([
        refreshForecasts(data.variantIds),
        refreshVariants(data.variantIds),
        refreshStockOutTimes(data.variantIds),
      ])

      utils.variantTableData.settings.setData(undefined, (oldData) =>
        mergeMap(oldData, data.settings),
      )

      invalidateMembershipQueries(utils, [
        variables.sourceCollectionId,
        variables.targetCollectionId,
      ])

      const targetCollection = utils.collection.getAllWithDetails
        .getData({})
        ?.find((c) => c.id === variables.targetCollectionId)

      showMovedToCollectionToast(
        targetCollection?.name ?? "collection",
        variables.targetCollectionId,
        targetCollection?.collectionType,
      )
      trackEvent("Collection Updated", { Action: "Move Variants" })
    },
    onError: (error) => showErrorToast(error),
  })
}
